import { isDatabaseUnavailableError } from '../../../../shared/localDb';
import { supabase } from '../../../../shared/supabase/supabaseClient';
import { normalizeUnitOfMeasure } from '@shared/quantity';
import type { BranchStockActivityRow } from '../../domain/stockTypes';
import { getStockMovementReasonLabel } from '../../domain/stockReasons';
import type { GetStockMovementHistoryOptions } from '../../domain/stockTypes';
import { resolveVariantLabel } from '../guards/stockInventoryGuards';

/**
 * ADR-017 Fase 4: SQLite eliminado como path de datos operacional.
 * `db` es un stub que retorna `[]` en todas las plataformas.
 * El feed de actividad de stock usa Supabase como fuente autoritativa,
 * filtrando por sucursal en servidor (join `stocks!inner` en movimientos,
 * OR `from/to_branch_id` en transferencias). Solo se consulta cuando el
 * caller tiene `useRemoteTeamHistory` (dueño de la organización): los
 * empleados ven el feed restringido, sin expandir acceso (RLS ya limita).
 */

/** Máximo de filas en el feed de actividad reciente por sucursal. */
export const BRANCH_ACTIVITY_FETCH_LIMIT = 25;

/** Etiqueta de actividad según rol de sucursal y estado del traspaso (evita «recibida» cuando sigue en `sent`). */
const transferActivityDescription = (isOriginBranch: boolean, status: string): string => {
  if (isOriginBranch) {
    switch (status) {
      case 'sent':
        return 'Transferencia enviada. Pendiente en destino';
      case 'completed':
        return 'Transferencia completada';
      case 'received':
        return 'Transferencia recibida en destino';
      case 'rejected':
        return 'Transferencia rechazada. Volvió a esta sucursal';
      default:
        return 'Transferencia';
    }
  }
  switch (status) {
    case 'sent':
      return 'Transferencia pendiente de recepción';
    case 'completed':
    case 'received':
      return 'Transferencia recibida';
    case 'rejected':
      return 'Transferencia rechazada';
    default:
      return 'Transferencia';
  }
};

type ProductLabel = { name: string; unit_of_measure: string | null };

async function fetchProductLabels(productIds: string[]): Promise<Map<string, ProductLabel>> {
  if (productIds.length === 0) return new Map();
  const { data: products, error } = await supabase
    .from('products')
    .select('id, name, unit_of_measure')
    .in('id', productIds);
  if (error) throw error;
  return new Map((products ?? []).map((p) => [p.id as string, p as ProductLabel]));
}

type RemoteMovementRow = {
  id: string;
  delta: number;
  reason: string | null;
  created_at: string;
  product_name: string;
  product_id: string;
  unit_of_measure: string | null;
  variant_id: string | null;
};

async function fetchRemoteBranchMovements(
  branchId: string,
  limit: number
): Promise<RemoteMovementRow[]> {
  // Filtro de sucursal en servidor: antes traía los últimos limit*2
  // movimientos de TODA la org y filtraba client-side por stock.branch_id —
  // una sucursal con actividad podía quedar en 0 filas. Con `stocks!inner` +
  // `.eq('stocks.branch_id')` Postgres filtra antes del LIMIT.
  const { data, error } = await supabase
    .schema('inventory')
    .from('stock_movements')
    .select('id, delta, reason, created_at, stock_id, stocks!inner(branch_id, product_id, variant_id)')
    .eq('stocks.branch_id', branchId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) throw error;

  const rows = (data ?? []) as Array<{
    id: string;
    delta: number;
    reason: string | null;
    created_at: string;
    stock_id: string;
    // PostgREST tipa el embed como array (1:1 por FK, viene con 1 fila).
    stocks:
      | { branch_id: string; product_id: string | null; variant_id: string | null }
      | Array<{ branch_id: string; product_id: string | null; variant_id: string | null }>
      | null;
  }>;
  const stockOf = (r: (typeof rows)[number]) =>
    Array.isArray(r.stocks) ? r.stocks[0] ?? null : r.stocks;
  if (rows.length === 0) return [];

  const productIds = [
    ...new Set(
      rows.map((r) => stockOf(r)?.product_id).filter((v): v is string => Boolean(v))
    ),
  ];
  const productById = await fetchProductLabels(productIds);

  return rows
    .map((r) => {
      const stock = stockOf(r);
      const productId = stock?.product_id;
      if (!productId) return null;
      const product = productById.get(productId);
      return {
        id: r.id,
        delta: r.delta,
        reason: r.reason,
        created_at: r.created_at,
        product_name: product?.name ?? '—',
        product_id: productId,
        unit_of_measure: product?.unit_of_measure ?? null,
        variant_id: stock?.variant_id ?? null,
      };
    })
    .filter((r): r is NonNullable<typeof r> => r !== null);
}

type RemoteTransferRow = {
  id: string;
  product_id: string;
  from_branch_id: string;
  to_branch_id: string;
  quantity: number;
  performed_at: string;
  status: string;
  product_name: string;
  unit_of_measure: string | null;
  variant_id: string | null;
};

async function fetchRemoteBranchTransfers(
  branchId: string,
  limit: number
): Promise<RemoteTransferRow[]> {
  // Enviadas y recibidas por esta sucursal, filtradas en servidor.
  const { data, error } = await supabase
    .schema('inventory')
    .from('stock_transfers')
    .select('id, product_id, from_branch_id, to_branch_id, quantity, performed_at, status, variant_id')
    .or(`from_branch_id.eq."${branchId}",to_branch_id.eq."${branchId}"`)
    .order('performed_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  const rows = (data ?? []) as Array<{
    id: string;
    product_id: string | null;
    from_branch_id: string;
    to_branch_id: string;
    quantity: number;
    performed_at: string;
    status: string;
    variant_id: string | null;
  }>;
  if (rows.length === 0) return [];

  const productIds = [
    ...new Set(rows.map((r) => r.product_id).filter((v): v is string => Boolean(v))),
  ];
  const productById = await fetchProductLabels(productIds);

  return rows
    .map((r) => {
      if (!r.product_id) return null;
      const product = productById.get(r.product_id);
      return {
        id: r.id,
        product_id: r.product_id,
        from_branch_id: r.from_branch_id,
        to_branch_id: r.to_branch_id,
        quantity: r.quantity,
        performed_at: r.performed_at,
        status: r.status,
        product_name: product?.name ?? '—',
        unit_of_measure: product?.unit_of_measure ?? null,
        variant_id: r.variant_id ?? null,
      };
    })
    .filter((r): r is NonNullable<typeof r> => r !== null);
}

export const getBranchStockActivity = async (
  branchId: string,
  limit: number,
  options?: GetStockMovementHistoryOptions
): Promise<BranchStockActivityRow[]> => {
  // Restricción: el historial remoto de equipo (movimientos + traspasos de
  // todas las sucursales) solo lo consulta el dueño de la organización.
  // El caller muestra copy de restricción; acá no se expande el acceso.
  if (options?.useRemoteTeamHistory !== true) return [];

  let movementRows: RemoteMovementRow[] = [];
  let transferRows: RemoteTransferRow[] = [];
  try {
    // Errores propagan al loader: el feed muestra estado de error + reintento,
    // nunca un "Sin actividad" falso.
    [movementRows, transferRows] = await Promise.all([
      fetchRemoteBranchMovements(branchId, limit),
      fetchRemoteBranchTransfers(branchId, limit),
    ]);
  } catch (error) {
    if (!isDatabaseUnavailableError(error)) {
      throw error;
    }
    return [];
  }

  const movementItems: BranchStockActivityRow[] = movementRows.map((row) => ({
    type: 'movement' as const,
    id: row.id,
    date: row.created_at,
    productName: row.product_name,
    productId: row.product_id,
    description: getStockMovementReasonLabel(row.reason),
    delta: row.delta,
    unitOfMeasure: normalizeUnitOfMeasure(row.unit_of_measure),
    variantId: row.variant_id ?? null,
    variantLabel: row.variant_id ? resolveVariantLabel(row.variant_id) : null,
  }));

  const transferItems: BranchStockActivityRow[] = transferRows.map((row) => {
    const isOut = row.from_branch_id === branchId;
    const description = transferActivityDescription(isOut, row.status);
    return {
      type: 'transfer' as const,
      id: row.id,
      date: row.performed_at,
      productName: row.product_name,
      productId: row.product_id,
      description,
      quantity: row.quantity,
      toBranchId: row.to_branch_id,
      fromBranchId: row.from_branch_id,
      status: row.status,
      unitOfMeasure: normalizeUnitOfMeasure(row.unit_of_measure),
      variantId: row.variant_id ?? null,
      variantLabel: row.variant_id ? resolveVariantLabel(row.variant_id) : null,
    };
  });

  const merged = [...movementItems, ...transferItems].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
  return merged.slice(0, limit);
};
